import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { imgUrl } from '../utils';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import './ProductDetail.css';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const { isBuyer } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then(r => {
      setProduct(r.data);
      setQty(r.data.min_order_qty || 1);
      setLoading(false);
    }).catch(() => { setLoading(false); navigate('/catalogue'); });
  }, [id, navigate]);

  const handleAdd = () => {
    if (!product) return;
    addItem({ ...product, primary_image: product.images?.[0]?.filename }, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;
  if (!product) return null;

  const images = product.images || [];
  const primaryImg = images[activeImg];

  return (
    <div className="product-detail">
      <div className="pd-breadcrumb container">
        <Link to="/catalogue">Catalogue</Link>
        {product.parent_category_name && <><span> / </span><Link to={`/catalogue?category=${product.parent_category_slug}`}>{product.parent_category_name}</Link></>}
        <span> / </span>
        <Link to={`/catalogue?category=${product.category_slug}`}>{product.category_name}</Link>
        <span> / </span>
        <span>{product.name}</span>
      </div>

      <div className="pd-body container">
        {/* IMAGES */}
        <div className="pd-images">
          <div className="pd-main-img">
            {primaryImg
              ? <img src={imgUrl(primaryImg.filename)} alt={product.name} />
              : <div className="pd-img-placeholder"><span>Coming Soon</span></div>
            }
          </div>
          {images.length > 1 && (
            <div className="pd-thumbs">
              {images.map((img, i) => (
                <div key={img._id} className={`pd-thumb ${i === activeImg ? 'active' : ''}`} onClick={() => setActiveImg(i)}>
                  <img src={imgUrl(img.filename)} alt="" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div className="pd-info">
          <p className="tag">{product.parent_category_name} · {product.category_name}</p>
          <h1 className="pd-title">{product.name}</h1>
          <div className="divider" />

          {product.price_visible ? (
            <div className="pd-price">
              <span className="pd-price-label">Wholesale Price</span>
              <span className="pd-price-value">£{Number(product.price).toFixed(2)}</span>
              <span className="pd-price-unit">per unit</span>
            </div>
          ) : (
            <div className="pd-price-locked">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              <div>
                <p className="pd-locked-title">Price visible to Authorised Buyers</p>
                <p className="pd-locked-sub"><Link to="/register">Register your business</Link> or <Link to="/login">log in</Link> to see pricing.</p>
              </div>
            </div>
          )}

          <div className="pd-meta">
            <div className="pd-meta-row"><span>Minimum Order</span><strong>{product.min_order_qty} units</strong></div>
            {product.sizes && <div className="pd-meta-row"><span>Sizes</span><strong>{product.sizes}</strong></div>}
            {product.colours && <div className="pd-meta-row"><span>Colours</span><strong>{product.colours}</strong></div>}
            <div className="pd-meta-row">
              <span>Availability</span>
              <strong className={product.stock_status === 'available' ? 'status-available' : 'status-soon'}>
                {product.stock_status === 'available' ? 'In Stock' : product.stock_status === 'coming_soon' ? 'Coming Soon' : 'Out of Stock'}
              </strong>
            </div>
          </div>

          {product.description && (
            <div className="pd-desc">
              <p>{product.description}</p>
            </div>
          )}

          {isBuyer && product.stock_status === 'available' && (
            <div className="pd-order">
              <div className="pd-qty">
                <label>Quantity</label>
                <div className="qty-controls">
                  <button onClick={() => setQty(q => Math.max(product.min_order_qty, q - product.min_order_qty))}>−</button>
                  <input type="number" value={qty} min={product.min_order_qty} step={product.min_order_qty}
                    onChange={e => setQty(Math.max(product.min_order_qty, parseInt(e.target.value) || product.min_order_qty))} />
                  <button onClick={() => setQty(q => q + product.min_order_qty)}>+</button>
                </div>
                <p className="qty-note">Minimum order: {product.min_order_qty} units</p>
              </div>
              <button className={`btn-terracotta pd-add-btn ${added ? 'added' : ''}`} onClick={handleAdd}>
                {added ? '✓ Added to Basket' : 'Add to Basket'}
              </button>
              <Link to="/basket" className="btn-secondary" style={{justifyContent:'center'}}>View Basket</Link>
            </div>
          )}

          {!isBuyer && (
            <div className="pd-register-cta">
              <p>Want to order this product?</p>
              <Link to="/register" className="btn-terracotta">Register as Authorised Buyer</Link>
            </div>
          )}

          <div className="pd-bank-info">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Orders are paid by bank transfer. A proforma invoice will be issued on confirmation.
          </div>
        </div>
      </div>

      <div className="pd-back container">
        <Link to="/catalogue" className="btn-secondary btn-sm">← Back to Catalogue</Link>
      </div>
    </div>
  );
}
